import { CreateVariantDTO, CreateVariantsControllerDTO, ProductsVariantsApi } from '../generated'
import { Product, ProductResponse, ApiFilters, ProductImage, FullImage, ProductVariant } from '../types'
import { http } from './http'

export const productVariantsApi = new ProductsVariantsApi(undefined, `${http.http.defaults.baseURL}/v2`, http.http)

export const fetchProducts = async ({ limit, skip, query, filters, sort }: ApiFilters): Promise<ProductResponse> => {
  const { data } = await http.get<ProductResponse>(`/v2/products`, {
    params: {
      limit,
      skip,
      query,
      filters,
      sort
    }
  })
  return data
}

export async function fetchProduct(id: string): Promise<Product> {
  const { data } = await http.get<Product>(`/v2/products/${id}`)
  return data
}

export const copyProduct = async (id: string): Promise<Product> => {
  const { data } = await http.post<Product>(`/v2/products/${id}/copy`)
  return data
}

export const updateProduct = async (id: string, payload: Partial<Product>): Promise<Product> => {
  const { data } = await http.patch<Partial<Product>>(`/v2/products/${id}`, payload)
  return data as unknown as Product
}

export const deleteProduct = async (id: string): Promise<Product> => {
  const { data } = await http.delete<Product>(`/v2/products/${id}`)
  return data
}

export const fetchProductImages = async (id: string): Promise<ProductImage[]> => {
  const { data } = await http.get<ProductImage[]>(`/v2/products/${id}/images`)
  return data
}

export const fetchProductSizes = async (productId: string, sizeSpecId: string): Promise<Array<string>> => {
  const { data } = await http.get<Array<string>>(`/v2/products/${productId}/size-spec/${sizeSpecId}/sizes`)
  return data
}

export const fetchProductVariants = async (productId: string): Promise<ProductVariant[]> => {
  const { data } = await http.get<ProductVariant[]>(`/v2/products/${productId}/variants`)
  return data
}

export const deleteProductVariant = async (productId: string, variantId: string) => {
  const { data } = await http.delete<Array<ProductVariant>>(`/v2/products/${productId}/variants/${variantId}`)
  return data
}

export const bulkDeleteProductVariants = async (productId: string, variantIds: Array<string>) => {
  const { data } = await http.delete<Array<ProductVariant>>(`/v2/products/${productId}/variants/bulk`, {
    data: variantIds
  })
  return data
}

export async function createProductVariant(
  productId: string,
  variant: CreateVariantDTO
): Promise<Array<ProductVariant>> {
  const { data } = await productVariantsApi.createProductVariant(productId, variant)
  return data
}

export async function bulkCreateProductVariants(
  productId: string,
  variants: CreateVariantsControllerDTO['variants']
): Promise<Array<ProductVariant>> {
  const { data } = await productVariantsApi.bulkCreateProductVariants(productId, { variants })
  return data
}

export const updateProductVariant = async (productId: string, variantId: string, updates: Partial<ProductVariant>) => {
  const { data } = await http.patch(`/v2/products/${productId}/variants/${variantId}`, updates)
  return data
}

export const bulkUpdateProductVariants = async (
  productId: string,
  updates: Array<Partial<ProductVariant>>,
  reorder = false
) => {
  const { data } = await http.patch(`/v2/products/${productId}/variants/bulk`, { updates, reorder })
  return data
}

export const createProduct = async (product: Partial<Product>): Promise<Product> => {
  const { data } = await http.post(`/v2/products/`, product)
  return data as Product
}

export async function patchImage(productId: string, imageId: string, update: Partial<FullImage>): Promise<FullImage> {
  const { data } = await http.patch(`v2/products/${productId}/images/${imageId}`, update)
  return data as FullImage
}
